import { ErrorMessage, Input, Label } from 'components/Form'
import { ResultsTray } from 'components/GenericSearch/ResultsTray'
import { BasicInputProps } from 'components/UserInputs'
import { useClickOutside } from 'hooks'
import { useDebouncedSearch } from 'hooks/useDebouncedSearch'
import { usePlaces } from 'hooks/usePlaces'
import { useConfig } from 'providers'
import {
  ChangeEvent,
  FocusEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Box } from 'theme-ui'
import { GooglePlaceResult, GooglePlaceSearchResults } from 'types/location'
import { noop } from 'utils'
import { GooglePlacesListItem } from './ListItem'
import googleAttribution from './powered_by_google.png'

export interface PlacesAutoCompleteInputProps extends BasicInputProps {
  name: string
  countryCode?: string
  onPlaceSelected: (place: GooglePlaceResult) => void
  error?: string
  placeholder?: string
  tabIndex?: number
}

export const PlacesAutocompleteInput = ({
  name,
  label,
  value,
  placeholder,
  countryCode,
  onPlaceSelected,
  error: formError,
  onChange = noop,
  onBlur = noop,
  tabIndex,
}: PlacesAutoCompleteInputProps) => {
  const config = useConfig()
  const isPublicCountry =
    config &&
    !!config.publicCountries.find(
      (publicCountry) => publicCountry.code === countryCode
    )
  const searchBoxRef = useRef<HTMLDivElement>(null)
  const googleAttributeRef = useRef<HTMLDivElement>(null)
  const { query, select } = usePlaces(googleAttributeRef)
  const getResults = useCallback(
    async ({ query: searchQuery }) => {
      try {
        const results = await query({ query: searchQuery, countryCode })
        return { results }
      } catch (e) {
        throw e
      }
    },
    [query, countryCode]
  )
  const [error, setError] = useState<string>('')
  useEffect(() => {
    if (formError) {
      setError(formError)
    } else {
      setError('')
    }
  }, [formError])

  const [results, search, state] = useDebouncedSearch(getResults)

  const [showResults, setShowResults] = useState<boolean>(false)
  const closeResultsTray = useCallback(
    () => setShowResults(false),
    [setShowResults]
  )
  const openResultsTray = useCallback(
    () => setShowResults(true),
    [setShowResults]
  )

  useClickOutside(searchBoxRef, closeResultsTray)

  useEffect(() => {
    if (state.matches('idle')) {
      closeResultsTray()
    } else if (
      state.matches('ready.debouncing') ||
      state.matches('ready.searching') ||
      state.matches('ready.success')
    ) {
      openResultsTray()
    } else if (state.matches('ready.failed')) {
      closeResultsTray()
      setError(state.context.error?.message ?? '')
    } else {
      closeResultsTray()
    }
  }, [state, setError, closeResultsTray, openResultsTray])

  const handleChange = (evt: ChangeEvent<any>) => {
    const {
      target: { value },
    } = evt
    onChange(evt)
    if (isPublicCountry) {
      search({ query: value })
    }
    if (value.length > 2) {
      setError('')
    }
  }

  const handleBlur = (evt: FocusEvent<HTMLInputElement>) => {
    onBlur(evt)
  }
  const handleItemClick = async (item: GooglePlaceSearchResults) => {
    try {
      const placeData = await select(item.place_id)
      onPlaceSelected(placeData)
      closeResultsTray()
    } catch (e: any) {
      setError('Failed to load autocomplete data.')
      closeResultsTray()
    }
  }
  return (
    <>
      <Label htmlFor={name}>{label}</Label>
      <Box sx={{ position: 'relative' }} onBlur={handleBlur} ref={searchBoxRef}>
        <Input
          autoComplete={isPublicCountry ? 'new-password' : 'on'}
          id={name}
          name={name}
          placeholder={placeholder}
          onChange={handleChange}
          value={value}
          aria-invalid={!!error}
          tabIndex={tabIndex}
        />
        <Box
          sx={{
            position: ['relative', 'absolute'],
            ml: [2, 10],
            left: [0, '100%'],
            bottom: [-1, 0],
          }}
        >
          {isPublicCountry && (
            <img src={googleAttribution} alt="Powered by Google" />
          )}
          <Box ref={googleAttributeRef}></Box>
        </Box>
        <ResultsTray
          ItemCard={GooglePlacesListItem}
          variant="search"
          results={results}
          show={showResults}
          isLoading={
            state.matches('ready.debouncing') ||
            state.matches('ready.searching')
          }
          onSearchItemClick={handleItemClick}
        />
      </Box>
      <ErrorMessage>{error ? <span>{error}</span> : null}</ErrorMessage>
    </>
  )
}
